import React, { useState, useMemo } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup } from "react-leaflet";
import { useQuery } from "@tanstack/react-query";
import { MapPin, Search, Eye, Loader2, Info } from "lucide-react";
import { api } from "../lib/api";

const UK_CENTER = [54.5, -3.0];
const TILE_MIN_ZOOM = 4;
const TILE_MAX_ZOOM = 12;
// OSM standard tiles support zoom 0-19 across the whole UK — the branded OS
// Leisure layer used elsewhere only serves zoom 7+, which is fine for the
// walker-facing map but crops out the national overview an admin needs here.
const OSM_TILE_URL = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
const OSM_ATTRIBUTION = "&copy; OpenStreetMap contributors";

const WINDOWS = [
  { value: 7, label: "7 days" },
  { value: 30, label: "30 days" },
  { value: 90, label: "90 days" },
];

// Radius/intensity scale — proportional to a point's total activity so a
// hot region visibly outsizes a quiet one, but everything stays clickable.
function scaleRadius(total, max) {
  if (!max || max <= 0) return 8;
  const t = Math.min(1, total / max);
  return 8 + Math.round(t * 22); // 8-30px
}
function scaleOpacity(total, max) {
  if (!max || max <= 0) return 0.4;
  const t = Math.min(1, total / max);
  return 0.35 + t * 0.5; // 0.35-0.85
}

/**
 * Admin region heatmap.
 *
 * Renders a Leaflet UK map with a CircleMarker per region whose radius and
 * opacity are proportional to combined `map_search` + view activity. Reuses
 * the OS Leisure tile config from /map/os-config with an OSM fallback so
 * the map still renders if the OS token isn't wired up in the environment.
 */
export default function AdminRegionHeatMap() {
  const [days, setDays] = useState(90);

  const heatQ = useQuery({
    queryKey: ["admin-region-heat", days],
    queryFn: async () => (await api.get("/admin/telemetry/heatmap", { params: { days } })).data,
    refetchOnWindowFocus: false,
  });

  const points = heatQ.data?.points || [];
  const max = heatQ.data?.max_intensity || 0;
  const unmapped = heatQ.data?.unmapped || [];
  const unmappedTotal = heatQ.data?.unmapped_total || 0;
  const unknown = heatQ.data?.unknown_count || 0;
  const grandTotal = useMemo(
    () => points.reduce((acc, p) => acc + p.total, 0),
    [points]
  );

  return (
    <div className="td-card p-5 sm:p-6" data-testid="region-heatmap-card">
      <div className="flex items-center gap-2 flex-wrap">
        <MapPin size={16} className="text-brand-accent" />
        <h2 className="font-heading text-lg font-medium">Where walkers are looking</h2>
        <span className="ml-auto text-xs text-brand-subtle">
          {grandTotal.toLocaleString("en-GB")} plotted event{grandTotal === 1 ? "" : "s"} · bots excluded
        </span>
      </div>

      <div className="mt-3 flex items-center gap-1.5 flex-wrap" data-testid="region-heatmap-window">
        {WINDOWS.map((w) => (
          <button
            key={w.value}
            data-testid={`region-heatmap-window-${w.value}`}
            onClick={() => setDays(w.value)}
            className={"td-chip cursor-pointer " + (days === w.value ? "td-chip-active" : "")}
          >
            {w.label}
          </button>
        ))}
        {heatQ.isFetching && (
          <span className="text-xs text-brand-subtle inline-flex items-center gap-1 ml-2">
            <Loader2 size={12} className="animate-spin" /> refreshing
          </span>
        )}
      </div>

      <div
        className="mt-3 rounded-2xl overflow-hidden border border-brand-border/60 relative"
        style={{ height: 380 }}
        data-testid="region-heatmap-canvas"
      >
        <MapContainer
          center={UK_CENTER}
          zoom={5}
          minZoom={TILE_MIN_ZOOM}
          maxZoom={TILE_MAX_ZOOM}
          scrollWheelZoom
          style={{ height: "100%", width: "100%" }}
        >
          <TileLayer
            url={OSM_TILE_URL}
            attribution={OSM_ATTRIBUTION}
            minZoom={TILE_MIN_ZOOM}
            maxZoom={TILE_MAX_ZOOM}
          />
          {points.map((p) => (
            <CircleMarker
              key={p.matched_key}
              center={[p.lat, p.lng]}
              radius={scaleRadius(p.total, max)}
              pathOptions={{
                color: "#8A3E1E",
                weight: 1.5,
                fillColor: "#B16B3C",
                fillOpacity: scaleOpacity(p.total, max),
              }}
            >
              <Popup>
                <div className="text-sm" data-testid={`region-heatmap-popup-${p.matched_key}`}>
                  <div className="font-semibold text-brand-text">{p.region}</div>
                  <div className="text-xs text-brand-subtle mt-0.5">
                    {p.total.toLocaleString("en-GB")} event{p.total === 1 ? "" : "s"} · last {days} days
                  </div>
                  <div className="mt-1.5 flex flex-col gap-0.5 text-xs">
                    <span className="inline-flex items-center gap-1.5">
                      <Search size={11} className="text-brand-primary" /> {p.searches} map search{p.searches === 1 ? "" : "es"}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <Eye size={11} className="text-brand-accent" /> {p.views} view{p.views === 1 ? "" : "s"}
                    </span>
                  </div>
                  {p.regions.length > 1 && (
                    <div className="text-[10px] text-brand-subtle mt-1.5 italic">
                      Merged from: {p.regions.join(", ")}
                    </div>
                  )}
                </div>
              </Popup>
            </CircleMarker>
          ))}
        </MapContainer>

        {/* Empty overlay */}
        {!heatQ.isLoading && points.length === 0 && (
          <div className="absolute inset-0 bg-brand-sand/85 flex items-center justify-center p-6 z-[500]">
            <div className="text-center max-w-sm">
              <MapPin size={22} className="text-brand-subtle mx-auto" />
              <p className="text-sm text-brand-text mt-2 font-medium">No mapped activity yet</p>
              <p className="text-xs text-brand-subtle mt-1">
                Region-tagged searches and trail views will appear here.
                {unknown > 0 && ` (${unknown} events had no region tag.)`}
              </p>
            </div>
          </div>
        )}

        {heatQ.isLoading && (
          <div className="absolute inset-0 bg-brand-sand/70 flex items-center justify-center z-[500]">
            <Loader2 size={20} className="animate-spin text-brand-primary" />
          </div>
        )}
      </div>

      {/* Legend + transparency footnote */}
      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] text-brand-subtle">
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-2.5 h-2.5 rounded-full bg-[#B16B3C]/45 border border-[#8A3E1E]" />
          quiet
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-4 h-4 rounded-full bg-[#B16B3C]/85 border border-[#8A3E1E]" />
          hot
        </span>
        {(unknown > 0 || unmappedTotal > 0) && (
          <span className="inline-flex items-center gap-1 ml-auto" title="Events without a matched UK region — usually anonymous browsers or unrecognised region strings.">
            <Info size={11} />
            {unknown > 0 && (
              <span data-testid="region-heatmap-unknown">
                {unknown.toLocaleString("en-GB")} untagged
              </span>
            )}
            {unmappedTotal > 0 && (
              <span data-testid="region-heatmap-unmapped">
                {" · "}{unmappedTotal.toLocaleString("en-GB")} unmapped
                {unmapped.length > 0 && ` (${unmapped.slice(0, 3).map((u) => u.region).join(", ")}${unmapped.length > 3 ? "…" : ""})`}
              </span>
            )}
          </span>
        )}
      </div>
    </div>
  );
}
